
import { useEffect, useState } from 'react';
import infoData from '@/config/INFO.json';
import {
    Headphones, Send,
    Megaphone, Gavel, Radio, Palette,
    Users, BookOpen, Gamepad2, Trophy, Sparkles,
    Info, ShieldCheck
} from 'lucide-react';
import { GramIcon } from '@/components/ui/GramIcon';
import { homeApi } from '@/utils/api';
import { motion } from 'framer-motion';

export const Footer = ({ onNavigate }: { onNavigate?: (page: string, params?: any) => void }) => {
    const [config, setConfig] = useState<any>(infoData);
    const [providers, setProviders] = useState<any[]>([]);

    useEffect(() => {
        setConfig(infoData);
        // Fetch Providers
        homeApi.getSections()
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    const prov = data.sections.find((s: any) => s.id === 'providers');
                    if (prov) setProviders(prov.data);
                }
            })
            .catch(err => console.error("Footer Providers Fetch Error:", err));
    }, []);

    const { channels, info } = config;

    return (
        <div className="mt-8 px-4 pb-32 pt-12 bg-tg-bg text-white relative z-10">

            {/* 0. Providers Section (TonPlay Style) */}
            {providers.length > 0 && (
                <div className="mb-12">
                    <div className="flex items-center gap-2 mb-6 px-2">
                        <div className="w-8 h-8 rounded-lg glass-chip flex items-center justify-center border border-orange-500/20">
                            <Sparkles size={16} className="text-orange-500" />
                        </div>
                        <h3 className="text-sm font-black uppercase tracking-[0.2em] text-white">Our Providers</h3>
                    </div>

                    <div className="flex overflow-x-auto gap-3 px-2 pb-4 no-scrollbar -mx-2">
                        {providers.map((provider) => (
                            <motion.div
                                key={provider.id}
                                whileTap={{ scale: 0.94 }}
                                onClick={() => {
                                    if (onNavigate) onNavigate('all_items', { category: provider.title, packageId: provider.id });
                                }}
                                className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 glass-chip rounded-2xl border border-white/5 p-1.5 flex items-center justify-center group cursor-pointer transition-all hover:border-blue-500/30 snap-start"
                            >
                                <div className="w-full h-full rounded-xl overflow-hidden bg-black/20 flex items-center justify-center">
                                    <img
                                        src={provider.cover}
                                        alt={provider.title}
                                        className="w-full h-full object-cover grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).src = '/icons/default-avatar.svg';
                                        }}
                                    />
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            )}

            {/* 1. Support Card */}
            <div className="relative w-full glass-strong rounded-[20px] pl-5 pr-20 sm:pr-32 py-[1.2rem] overflow-hidden mb-6 shadow-lg border border-white/5">
                <div className="relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 shrink-0 rounded-xl glass-chip flex items-center justify-center border border-emerald-500/20">
                            <Headphones size={18} className="text-emerald-400" />
                        </div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm font-bold text-white leading-none">Support</h3>
                                <span className="bg-emerald-500/20 text-emerald-400 text-[9px] font-bold px-2 py-0.5 rounded-full border border-emerald-500/30">24/7</span>
                            </div>
                        </div>
                        <a
                            href={channels.support_bot}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Send a message"
                            className="ml-auto shrink-0 w-10 h-10 inline-flex items-center justify-center glass-chip hover:bg-white/10 text-emerald-400 rounded-xl transition-colors active:scale-95"
                        >
                            <Send size={18} />
                        </a>
                    </div>
                    <p className="text-[#8E8E93] text-[11px] font-medium leading-snug mt-1.5 pl-12">
                        Contact us if you have any questions
                    </p>
                </div>

                {/* Illustration (New Duck V4 Direct Final) */}
                <div className="absolute right-[-4px] bottom-[-4px] w-[5.5rem] h-[5.5rem] md:w-32 md:h-32 pointer-events-none flex items-center justify-center z-20">
                    <img
                        src="/assets/support-duck-v8.png"
                        alt="Support"
                        className="w-full h-full object-contain drop-shadow-2xl"
                        onError={(e) => {
                            // Fallback if local image fails
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.parentElement!.innerHTML = '<div class="text-7xl absolute bottom-4 right-4 animate-bounce">🎧</div>';
                        }}
                    />
                </div>
            </div>


        </div>
    );
};
